import { createContext, useCallback, useContext, useEffect, useMemo, useReducer } from 'react';
import { initialState, ISavedStates, reducer } from 'hooks/savedViews/reducer';
import { SavedStatesActions, SavedStatesActionCreator } from 'hooks/savedViews/actions';
import { SlykitContext } from '../../slykitContext';
import { del, get, post, put } from 'srs-middleware';

type TODO = any;

export const SavedStatesContext = createContext<{ state: ISavedStates; dispatch: TODO }>(null);

const getSavedViews = async () => {
	return await get(`/SRS/Application/Slykit/SavedViews`);
};

const postSavedView = async (id: string, view: Object) => {
	return await post(`/SRS/Application/Slykit/SavedViews/${id}`, JSON.stringify(view));
};

const putSavedView = async (id: string, view: Object) => {
	return await put(`/SRS/Application/Slykit/SavedViews/${id}`, JSON.stringify(view));
};

const deleteSavedView = async (id: string) => {
	return await del(`/SRS/Application/Slykit/SavedViews/${id}`);
};

export function useSavedStatesState(isReady) {
	const [state, dispatch] = useReducer(reducer, initialState);

	// load any saved views from the backend
	useEffect(() => {
		if (isReady) {
			getSavedViews()
				.then((views: string[]) => {
					const payload = {
						isSavedStateAvailable: true,
						dashboards: {},
						explorers: {},
						widgets: {},
						error: null,
					};
					if (views.length > 0) {
						const formattedViews = views
							.map((view) => {
								try {
									return JSON.parse(view);
								} catch (e) {
									console.warn(`ERROR PARSING VIEW: ${view}`);
									return null;
								}
							})
							.filter((view) => view !== null);
						formattedViews.forEach((view) => {
							const type = view.metaData.type;
							payload[`${type}s`][view.metaData.id] = view;
						});
					}
					dispatch(SavedStatesActionCreator.loadViews(payload));
				})
				.catch((e) => {
					const payload = {
						isSavedStateAvailable: false,
						dashboards: {},
						explorers: {},
						widgets: {},
						error: e,
					};
					console.warn(
						'SERBERUS: The server does not have saved views ' +
							'enabled. `useSavedStatesState` expects it to be available.'
					);
					dispatch(SavedStatesActionCreator.loadViews(payload));
				});
		}
	}, [isReady]);

	// fire off api actions before updating state
	const customDispatch = useCallback(
		(action) => {
			if (action.type === SavedStatesActions.SAVE_EXPLORER || action.type === SavedStatesActions.SAVE_WIDGET) {
				postSavedView(action.payload.id, action.payload.view).catch((e) => {
					dispatch(SavedStatesActionCreator.setError(e));
				});
			} else if (action.type === SavedStatesActions.NEW_DASHBOARD) {
			} else if (action.type === SavedStatesActions.UPDATE_VIEW) {
				putSavedView(action.payload.id, action.payload.view).catch((e) => {
					dispatch(SavedStatesActionCreator.setError(e));
				});
			} else if (action.type === SavedStatesActions.DELETE_VIEW) {
				deleteSavedView(action.payload.id).catch((e) => {
					dispatch(SavedStatesActionCreator.setError(e));
				});
			}
			return dispatch(action);
		},
		[dispatch]
	);

	return useMemo(() => {
		return { state, dispatch: customDispatch };
	}, [state, customDispatch]);
}

export function useSavedStateSelectors() {
	const { state } = useContext(SlykitContext);
	if (!state.features.isSaveViewsEnabled) return {};
	const {
		state: { isSavedStateAvailable, explorers: ogExplorers, widgets: ogWidgets, dashboards: ogDashboards },
		dispatch,
	} = useContext(SavedStatesContext);

	// Attach ids to dashboards
	const dashboards = useMemo(() => {
		return Object.entries(ogDashboards).map(([id, dashboard]) => ({
			...dashboard,
			metaData: {
				id,
				...dashboard.metaData,
			},
		}));
	}, [ogDashboards]);

	// Attach ids to widgets
	const widgets = useMemo(() => {
		return Object.entries(ogWidgets).map(([id, widget]) => ({
			...widget,
			metaData: {
				id,
				...widget.metaData,
			},
		}));
	}, [ogWidgets]);

	// Attach ids to explorers
	const explorers = useMemo(() => {
		return Object.entries(ogExplorers).map(([id, explorer]) => ({
			...explorer,
			metaData: {
				id,
				...explorer.metaData,
			},
		}));
	}, [ogExplorers]);

	return { isSavedStateAvailable, dashboards, explorers, widgets, dispatch };
}